import type { CityConfig } from "@/pages/city-template/types";

export const oshkoshConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Oshkosh",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "oshkosh",
  route: "/cities/oshkosh",
  title: "Emergency & Expedited Passport Services Oshkosh WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Oshkosh, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago, about 150 miles southeast. A+ BBB rated. Serving Winnebago County, Fox Valley, and UW-Oshkosh travelers.",
  h1: "Emergency & Expedited Passport Services in Oshkosh, WI",
  heroSubtitle: "Oshkosh sits in the heart of Wisconsin's Fox Valley, about 150 miles from the Chicago Passport Agency. Fast Passport Center's registered couriers deliver your documents to Chicago so you get a new or renewed passport as fast as 24 hours — whether you're a UW-Oshkosh student studying abroad, an EAA AirVenture attendee heading overseas, or a Fox Valley family planning international travel.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Oshkosh%20Wisconsin%20Lake%20Winnebago%20waterfront%20at%20golden%20hour%20with%20calm%20reflective%20water%20and%20sailboats%2C%20historic%20downtown%20architecture%20with%20warm%20amber%20tones%2C%20Fox%20River%20flowing%20through%20city%2C%20professional%20landscape%20photography%2C%20wide%20angle%20composition%20with%20shoreline%20and%20skyline%2C%20soft%20natural%20lighting%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=oshkosh-hero-001&orientation=landscape",
  heroImageAlt: "Oshkosh Wisconsin Lake Winnebago waterfront and downtown skyline at golden hour",
  geoPosition: "44.0247;-88.5426",
  geoPlacename: "Oshkosh, Wisconsin",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 150 miles from Oshkosh — about 2.5 hours via I-41 and I-94",
  },
  nearbyCitiesZips: [
    { name: "Downtown Oshkosh", zips: "54901, 54902, 54903" },
    { name: "North Oshkosh", zips: "54904" },
    { name: "Neenah", zips: "54956" },
    { name: "Menasha", zips: "54952" },
    { name: "Appleton", zips: "54911, 54913, 54914, 54915" },
    { name: "Fond du Lac", zips: "54935, 54937" },
    { name: "Ripon", zips: "54971" },
    { name: "Omro", zips: "54963" },
    { name: "Winneconne", zips: "54986" },
    { name: "Berlin", zips: "54923" },
    { name: "Kaukauna", zips: "54130" },
    { name: "Kimberly", zips: "54136" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Chicago Passport Agency — 150 miles from Oshkosh" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Fox Valley Hub", desc: "Serving Oshkosh, Neenah, Menasha, and the entire Winnebago County region" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Oshkosh case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Appleton WI", route: "/cities/appleton" },
    { name: "Expedited passport in Green Bay WI", route: "/cities/green-bay" },
    { name: "Urgent passport in Milwaukee WI", route: "/cities/milwaukee" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Chicago passport services", route: "/states/illinois" },
    { name: "Expedited passport in Neenah WI", route: "/cities/neenah" },
    { name: "Emergency passport in Fond du Lac WI", route: "/cities/fond-du-lac" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Oshkosh remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency. Oshkosh sits along the I-41 corridor, making overnight document shipping fast and reliable from the Fox Valley.",
  fpcOfficeDistance: "Serving all of Winnebago County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Oshkosh, Neenah, Menasha, and the Fox Valley region. From UW-Oshkosh students to EAA aviation enthusiasts, we help Winnebago County travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Oshkosh and nearby communities including Neenah, Menasha, Appleton, Fond du Lac, Ripon, and the entire Fox Valley corridor.",
  howItWorksIntro: "Most Oshkosh travelers complete everything online and ship documents overnight. At about 150 miles from Chicago, Fast Passport Center provides fast courier turnaround for Fox Valley residents — ideal for UW-Oshkosh study abroad students and Fox Valley families.",
  mistakesIntro: "Oshkosh travelers, including UW-Oshkosh students heading overseas and EAA members traveling internationally, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Oshkosh and Fox Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Oshkosh? Here are the most common ones we hear from Winnebago County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for UW-Oshkosh study abroad students",
        "Perfect for EAA international travelers",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from ATW or MKE",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 150 miles to Chicago (2.5-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Oshkosh is about 150 miles from the Chicago Passport Agency. Our couriers make same-day delivery possible, as fast as 24 hours.",
      bullets: ["Fox Valley coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in WI",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Oshkosh adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Oshkosh and Winnebago County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Oshkosh and Winnebago County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Oshkosh, Neenah, and Fox Valley residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago Agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Winnebago County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Oshkosh and Fox Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Oshkosh WI", description: "Urgent passport processing for Oshkosh, WI travelers via Chicago Passport Agency courier — about 150 miles from Oshkosh." },
    { name: "Passport Renewal Oshkosh WI", description: "Fast passport renewal for Oshkosh and Winnebago County, Wisconsin residents." },
    { name: "New Passport Oshkosh WI", description: "First-time passport application assistance for Oshkosh and Fox Valley residents." },
    { name: "Lost Passport Oshkosh WI", description: "Passport replacement for lost or stolen passports for Oshkosh and Fox Valley residents." },
    { name: "Child Passport Oshkosh WI", description: "Minor passport services for Oshkosh and Winnebago County families." },
    { name: "Name Change Passport Oshkosh WI", description: "Passport name change service for Oshkosh, WI residents." },
  ],
  testimonials: [
    {
      name: "Madison K.",
      location: "UW-Oshkosh Student",
      rating: 5,
      text: "I'm a nursing student doing a clinical rotation in Ireland. Realized my passport expired 5 days before departure. Fast Passport Center handled everything — I had my new passport in 36 hours. Lifesaver for a college student on a deadline.",
      service: "Emergency Passport",
    },
    {
      name: "Tom R.",
      location: "Neenah, WI",
      rating: 5,
      text: "EAA AirVenture volunteer heading to an aviation conference in Germany. My passport renewal was taking forever through the post office. Fast Passport Center couriered everything to Chicago and I had my passport in 4 days.",
      service: "Expedited Renewal",
    },
    {
      name: "Lisa H.",
      location: "Downtown Oshkosh",
      rating: 5,
      text: "Family trip to Costa Rica over Christmas break. All four of us needed passports and the kids needed in-person appointments. Fast Passport Center walked us through everything and had all four back in 6 days.",
      service: "Family Passports",
    },
    {
      name: "Derek P.",
      location: "Menasha, WI",
      rating: 5,
      text: "Work assignment in Japan came up with two weeks notice. No passport at all. Fast Passport Center helped me do my first-ever DS-11 at the Oshkosh post office, then rushed everything to Chicago. Received in 5 business days.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "osh-1",
      name: "Oshkosh Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1025 W 20th Ave",
      city: "Oshkosh",
      state: "WI",
      zip: "54902",
      phone: "(920) 236-6580",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "West side of Oshkosh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Oshkosh. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "osh-2",
      name: "Winnebago County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "415 Jackson St, Suite 140",
      city: "Oshkosh",
      state: "WI",
      zip: "54901",
      phone: "(920) 232-3364",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Downtown Oshkosh",
      website: "https://www.co.winnebago.wi.us/county-clerk",
      websiteLabel: "Winnebago County Clerk",
      notes: "Accepts passport applications for Winnebago County residents. Convenient downtown location.",
      icon: "ri-government-line",
    },
    {
      id: "osh-3",
      name: "Neenah Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "130 W Franklin Ave",
      city: "Neenah",
      state: "WI",
      zip: "54956",
      phone: "(920) 725-2821",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "7 miles east of Oshkosh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient alternative for Neenah and eastern Winnebago County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "osh-4",
      name: "Menasha Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "84 Racine St",
      city: "Menasha",
      state: "WI",
      zip: "54952",
      phone: "(920) 722-2301",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "6 miles northeast of Oshkosh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Menasha and northern Lake Winnebago communities.",
      icon: "ri-mail-line",
    },
    {
      id: "osh-5",
      name: "Fond du Lac Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "99 W 2nd St",
      city: "Fond du Lac",
      state: "WI",
      zip: "54935",
      phone: "(920) 921-5200",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "22 miles south of Oshkosh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Fond du Lac and southern Winnebago County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "osh-6",
      name: "Ripon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "220 Jefferson St",
      city: "Ripon",
      state: "WI",
      zip: "54971",
      phone: "(920) 748-3201",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "21 miles southwest of Oshkosh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Ripon and western Fond du Lac County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "150 miles — 2.5-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking a 2.5-Hour Drive to Chicago Is Easy",
      description: "Even though Chicago is only 150 miles away, the Chicago Passport Agency has appointment-only access. Walk-ins are turned away. A wasted 5-hour round trip is not how you want to spend your day. We handle the appointment and delivery for you.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2x2 inches with a white background. CVS and Walgreens in Oshkosh and Neenah are generally reliable, but always verify your photos meet the exact requirements.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Form — UW-Oshkosh Student Mistakes",
      description: "UW-Oshkosh students studying abroad often show up with DS-11 when they need DS-82, or vice versa. First-time passports use DS-11; renewals of adult passports use DS-82.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Proof of Travel Ready",
      description: "Emergency processing at Chicago requires confirmed travel documents. Have your flight itinerary or cruise booking ready before reaching out — especially important for EAA international travelers.",
    },
    {
      icon: "ri-parent-line",
      title: "Both Parents Required for Minors",
      description: "Minors under 16 require both parents to appear, or one with notarized Form DS-3053. Oshkosh and Neenah families with one parent working out of town often miss this requirement.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Despite living in the Fox Valley, Oshkosh residents using standard USPS processing still wait 8-11 weeks. UW-Oshkosh students should start passport applications at least 8 weeks before any study abroad departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Oshkosh, Neenah, or Menasha." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to Chicago — about 150 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Oshkosh or Fox Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Oshkosh from a passport agency?",
      a: "Oshkosh is approximately 150 miles from the Chicago Passport Agency — about a 2.5-hour drive via I-41 and I-94. However, the Chicago agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles the courier delivery so you don't need to make the trip.",
    },
    {
      q: "Can UW-Oshkosh students get fast passports for study abroad?",
      a: "Yes. UW-Oshkosh students studying abroad are some of our most frequent customers in the Fox Valley. We can process student passports in 2–9 business days with standard expedited service, or 24–48 hours for emergency processing. Start early — ideally 8 weeks before departure.",
    },
    {
      q: "How fast can I get an emergency passport in Oshkosh?",
      a: "Emergency processing is typically 24–48 hours for Oshkosh residents. Our couriers deliver directly to the Chicago Passport Agency, about 150 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Oshkosh have its own passport agency?",
      a: "No. There are no passport agencies in Wisconsin. The closest regional passport agency is in Chicago, about 150 miles from Oshkosh. Oshkosh does have acceptance facilities at the Main Post Office and Winnebago County Clerk for first-time applications requiring in-person submission.",
    },
    {
      q: "What if I need a passport for EAA AirVenture international travel?",
      a: "EAA AirVenture brings thousands of aviation enthusiasts to Oshkosh each summer, many of whom travel internationally year-round. Fast Passport Center serves the aviation community with expedited passport services — whether you're heading to an international air show, flying your own aircraft abroad, or attending aviation conferences overseas.",
    },
    {
      q: "Where do Oshkosh travelers fly internationally?",
      a: "Oshkosh travelers typically fly internationally through Appleton International Airport (ATW, 25 miles north), Milwaukee Mitchell (MKE, 90 miles south), or Chicago O'Hare (ORD, 160 miles south). ATW offers connections to major hubs. A valid U.S. passport is required for all international flights from all airports.",
    },
    {
      q: "How do I renew my passport in Oshkosh?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Does the Oshkosh post office do passport photos?",
      a: "The Oshkosh Main Post Office on W 20th Ave accepts passport applications but may not offer photo services. CVS Pharmacy, Walgreens, and FedEx Office in Oshkosh offer passport photo services. Always verify your photos meet the exact 2x2 inch specification before submitting your application.",
    },
    {
      q: "What documents do I need for a first-time passport in Oshkosh?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Oshkosh Main Post Office or Winnebago County Clerk.",
    },
    {
      q: "Can you serve Neenah and Menasha residents too?",
      a: "Yes. We serve all of Winnebago County including Neenah, Menasha, and the entire Fox Valley. Documents ship overnight from any UPS location in the Oshkosh-Neenah-Menasha area, and we deliver your passport back to your address.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Oshkosh", address: "1910 W 9th Ave, Oshkosh, WI 54904", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Oshkosh", address: "1725 W 9th Ave, Oshkosh, WI 54904", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Oshkosh", address: "600 N Main St, Oshkosh, WI 54901", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Neenah", address: "901 S Green Bay Rd, Neenah, WI 54956", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Neenah", address: "1170 S Koeller St, Oshkosh, WI 54902", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Oshkosh", address: "1665 W 20th Ave, Oshkosh, WI 54902", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Oshkosh", address: "300 S Koeller St, Oshkosh, WI 54902", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Winnebago County residents.", featured: true },
    { name: "WI DMV — Appleton", address: "924 S Oneida St, Appleton, WI 54915", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Full-service DMV for Outagamie and northern Winnebago County residents.", featured: false },
    { name: "WI DMV — Fond du Lac", address: "19 W Scott St, Fond du Lac, WI 54935", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Fond du Lac County and southern Winnebago County.", featured: false },
  ],
  airports: [
    {
      code: "ATW",
      name: "Appleton International Airport",
      distance: "25 miles north of Oshkosh",
      address: "W6390 Challenger Dr, Appleton, WI 54914",
      note: "Oshkosh's closest commercial airport with connections to major hubs via Delta, United, and American. About 30 minutes north via I-41. Passport required for international connections.",
      flightUrl: "https://www.atwairport.com/",
      flightLabel: "ATW Airport",
      featured: true,
    },
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "90 miles south of Oshkosh",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Full-service international airport with extensive domestic and international routes. About 1.5 hours south via I-41.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: false,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "160 miles south of Oshkosh",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub with extensive global connections. About 2.5 hours south via I-41 and I-94. Co-located with the Chicago Passport Agency.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Wisconsin Department of Health Services Vital Records office. Online, mail, or in-person options for Winnebago County residents.", url: "https://www.dhs.wisconsin.gov/vitalrecords/birth.htm" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Wisconsin Vital Records. Required for estate and legal matters in Winnebago County.", url: "https://www.dhs.wisconsin.gov/vitalrecords/death.htm" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Winnebago County Register of Deeds at 415 Jackson St. Certified copies for name changes and passport applications.", url: "https://www.co.winnebago.wi.us/register-of-deeds" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Winnebago County Clerk of Circuit Court. Required for name change passport corrections.", url: "https://www.wicourts.gov/courts/circuit/winnebago.htm" },
  ],
};